import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { Loader } from 'lucide-react';
import logger from '../../../utils/logger';

interface DragDropProps {
  onFileUpload: (file: File) => Promise<void>;
}

const DragDropCSV: React.FC<DragDropProps> = ({ onFileUpload }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  logger.log('🔧 DragDropCSV component rendered', {
    hasOnFileUpload: !!onFileUpload,
    isLoading,
    selectedFile: selectedFile?.name
  });

  const onDrop = useCallback(async (acceptedFiles: File[], rejectedFiles: any[]) => {
    logger.log('🎯 DragDropCSV - onDrop triggered!');
    logger.log('📁 Accepted files:', acceptedFiles);
    logger.log('❌ Rejected files:', rejectedFiles);

    // Log rejection details
    if (rejectedFiles.length > 0) {
      console.error('🚨 FILE REJECTED! Details:', {
        file: rejectedFiles[0].file,
        errors: rejectedFiles[0].errors,
        fileName: rejectedFiles[0].file?.name,
        fileType: rejectedFiles[0].file?.type,
        fileSize: rejectedFiles[0].file?.size
      });
    }

    const file = acceptedFiles[0];
    if (!file) {
      logger.log('⚠️ No file in acceptedFiles array');
      if (rejectedFiles.length > 0) {
        const rejectionReasons = rejectedFiles[0].errors.map((e: any) => e.message).join(', ');
        toast.error(`File rejected: ${rejectionReasons}`);
      }
      return;
    }

    logger.log('📄 File details:', {
      name: file.name,
      size: file.size,
      type: file.type
    });

    // Check file size (50MB limit to match parent component)
    if (file.size > 50 * 1024 * 1024) {
      logger.log('❌ File too large:', file.size);
      toast.error('File size should be less than 50MB');
      return;
    }

    // File type validation is handled by react-dropzone accept prop
    logger.log('✅ File type and size validation passed');

    logger.log('✅ File validation passed, starting upload...');
    setError('');
    setSelectedFile(file);
    setIsLoading(true);

    try {
      logger.log('📤 Calling onFileUpload callback...');
      await onFileUpload(file);
      logger.log('✅ onFileUpload completed successfully');
    } catch (err) {
      console.error('❌ Error in onFileUpload:', err);
      toast.error('Error processing file');
    } finally {
      setIsLoading(false);
      logger.log('🏁 onDrop completed');
    }
  }, [onFileUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDragEnter: () => logger.log('🔵 Drag Enter'),
    onDragLeave: () => logger.log('🔴 Drag Leave'),
    onDragOver: () => logger.log('🟢 Drag Over'),
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/csv': ['.csv'],
      'text/plain': ['.csv']
    },
    multiple: false,
    disabled: isLoading
  });

  logger.log('⚙️ Dropzone configured to accept: CSV, XLS, XLSX files');

  logger.log('🎨 isDragActive:', isDragActive);

  return (
    <div>
      <DropZoneStyle 
        {...getRootProps()} 
        $isDragActive={isDragActive} 
        $hasError={!!error}
        $isLoading={isLoading}
      >
        <input {...getInputProps()} />
        <div className="text-center">
          {isLoading ? (
            <>
              <Loader className="animate-spin" size={24} />
              <p style={{ marginTop: '10px' }}>Processing file...</p>
            </>
          ) : (
            <>
              <span role="img" aria-label="upload" style={{ fontSize: '2rem', marginBottom: '10px', display: 'block' }}>
                📤
              </span>
              {selectedFile ? (
                <p style={{ fontSize: '16px', color: '#3a4b86' }}>
                  Selected: {selectedFile.name}
                </p>
              ) : (
                <>
                  <p style={{ fontSize: '16px', marginBottom: '8px' }}>
                    {isDragActive ? 'Drop your file here' : 'Drag & Drop your CSV or Excel file here'}
                  </p>
                  <p style={{ fontSize: '14px', color: '#666' }}>or click to select a file (.csv, .xls, .xlsx)</p>
                </>
              )}
            </>
          )}
        </div>
      </DropZoneStyle>
      {error && (
        <ErrorMessage>{error}</ErrorMessage>
      )}
    </div>
  );
};

interface DropZoneStyleProps {
  $isDragActive: boolean;
  $hasError: boolean;
  $isLoading: boolean;
}

const DropZoneStyle = styled.div<DropZoneStyleProps>`
  border: 2px dashed ${props => props.$hasError ? '#dc2626' : props.$isDragActive ? '#3b82f6' : '#3a4b86'};
  border-radius: 8px;
  padding: 20px;
  width: 100%;
  cursor: ${props => props.$isLoading ? 'not-allowed' : 'pointer'};
  background: ${props => props.$isDragActive ? '#f8fafc' : 'white'};
  transition: all 0.2s ease;
  opacity: ${props => props.$isLoading ? '0.6' : '1'};

  &:hover {
    border-color: ${props => props.$hasError ? '#dc2626' : '#2a3b76'};
    background: #f8f9ff;
  }
`;

const ErrorMessage = styled.p`
  color: #dc2626;
  font-size: 14px;
  margin-top: 8px;
  text-align: center;
`;

export default DragDropCSV;